/**
 * The stage: the room, the camera rig, and the view presets that frame it.
 *
 * Orbit controls stay in the user's hands; the stage only takes the camera
 * back when a view is chosen or the viewport changes shape.
 */

import * as THREE from 'three';
import { type DeviceTier, deviceTier } from '../lib/device.ts';
import { ease } from '../lib/ease.ts';
import { type Tween, tween } from '../lib/tween.ts';
import { createControls } from './controls.ts';
import { createRoom } from './scene.ts';
import { type FitContext, framingFor, HOME_VIEW, VIEWS, type ViewName } from './views.ts';

/** Below this viewport aspect the deck turns a quarter turn to fill the screen. */
const PORTRAIT_ASPECT = 0.92;

export interface StageState {
  view: ViewName;
  layoutYaw: number;
  tier: DeviceTier;
}

export interface ApplyViewOptions {
  animate?: boolean;
  duration?: number;
}

export type Stage = ReturnType<typeof createStage>;

export function createStage(canvas: HTMLCanvasElement) {
  const tier = deviceTier();
  const room = createRoom(canvas, tier);
  const { renderer, scene } = room;

  const camera = new THREE.PerspectiveCamera(34, 1, 0.5, 600);
  camera.position.set(0, 80, 0.6);
  const controls = createControls(camera, canvas);

  const state: StageState = { view: HOME_VIEW, layoutYaw: 0, tier };
  const fitCtx = (): FitContext => ({ camera, canvas, layoutYaw: state.layoutYaw });

  /** The view change under way, if one is: only one may steer the camera. */
  let flight: Tween | null = null;

  function applyView(name: ViewName, { animate = true, duration = 900 }: ApplyViewOptions = {}) {
    const preset = VIEWS[name] ?? VIEWS[HOME_VIEW];
    state.view = name;
    const { position, target } = framingFor(fitCtx(), preset);

    /* A change still in flight is abandoned, and the controls it had taken
       are handed back — otherwise its remaining frames would keep dragging
       the camera toward a view nobody asked for any more. */
    flight?.cancel();
    flight = null;
    controls.enabled = true;

    if (!animate) {
      camera.position.copy(position);
      controls.target.copy(target);
      controls.update();
      return Promise.resolve(true);
    }

    const fromPos = camera.position.clone();
    const fromTarget = controls.target.clone();
    controls.enabled = false;
    const mine = tween({
      duration,
      ease: ease.inOutQuint,
      onUpdate: (t) => {
        camera.position.lerpVectors(fromPos, position, t);
        controls.target.lerpVectors(fromTarget, target, t);
        controls.update();
      },
      onComplete: () => {
        controls.enabled = true;
        if (flight === mine) flight = null;
      },
    });
    flight = mine;
    return mine;
  }

  function resize(): void {
    const w = canvas.clientWidth || innerWidth;
    const h = canvas.clientHeight || innerHeight;
    /* A collapsed or offscreen container reports 0x0. Framing from that gives
       a NaN aspect and strands the camera, so wait for a real size instead. */
    if (w <= 0 || h <= 0) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();

    const wantYaw = w / h < PORTRAIT_ASPECT ? -Math.PI / 2 : 0;
    state.layoutYaw = wantYaw;

    /* Both the fit and the safe-area shift depend on the viewport, so a partial
       update would leave the deck mis-framed: re-seat the camera entirely. */
    void applyView(state.view, { animate: false });
  }

  const observer = new ResizeObserver(() => {
    resize();
  });
  observer.observe(canvas);
  const onOrientation = (): void => {
    setTimeout(resize, 220);
  };
  addEventListener('orientationchange', onOrientation);

  return {
    renderer,
    scene,
    camera,
    controls,
    key: room.key,
    ground: room.ground,
    state,
    applyView,
    resize,
    render() {
      controls.update();
      renderer.render(scene, camera);
    },
    dispose() {
      observer.disconnect();
      removeEventListener('orientationchange', onOrientation);
      controls.dispose();
      room.dispose();
    },
  };
}
